// components/SocialsSectionMulti.tsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Typography, Input, Textarea, Button } from "@material-tailwind/react";
import {
  FaFacebookF,
  FaTiktok, 
} from "react-icons/fa"; 
import Script from "next/script";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquareFacebook } from "@fortawesome/free-brands-svg-icons";
import { config } from "@fortawesome/fontawesome-svg-core";
import "@fortawesome/fontawesome-svg-core/styles.css";
config.autoAddCss = false;

interface Props {
  tiktokPosts?: string[];
  facebookPage?: string;
  tiktokUsername?: string;  
}
 

interface TikMeta {
  url: string;
  thumbnail?: string | null;
  title?: string | null;
  description?: string | null;
  author_name?: string | null;
  author_avatar?: string | null;
  error?: string | null;
}

const DEFAULT_TIKTOKS = [
  "https://www.tiktok.com/@rebott.official/video/7532704566391917842",
  "https://www.tiktok.com/@rebott.official/video/7539075388228554001",
  "https://www.tiktok.com/@rebott.official/video/7536857445079977237",
];

function friendlyError(raw?: string | null) {
  if (!raw) return "ไม่สามารถโหลดตัวอย่าง";
  const s = raw.toLowerCase();
  if (s.includes("status")) return "ไม่สามารถโหลดตัวอย่าง (ข้อผิดพลาดจากเซิร์ฟเวอร์)";
  if (s.includes("pattern")) return "รูปแบบข้อมูลไม่ตรงกัน ขอลองใหม่อีกครั้ง";
  return "ไม่สามารถโหลดตัวอย่าง";
}

/** คืน hostname ยกเว้นกรณี www.tiktok.com จะคืนเป็น empty string */
function getHostNice(url?: string | null) {
  if (!url) return "";
  try {
    const h = new URL(url).hostname || "";
    if (h.toLowerCase() === "www.tiktok.com") return "";
    return h;
  } catch {
    return "";
  }
}

export default function SocialsSectionMulti({
  tiktokPosts = DEFAULT_TIKTOKS,
  facebookPage = "https://www.facebook.com/rebott.official",
  tiktokUsername = "rebott.official",
}: Props) {
  const safeTikUser = (tiktokUsername || "rebott.official").replace(/[^\w._]/g, "");
  const tiktokProfileUrl = `https://www.tiktok.com/@${safeTikUser}`;
  const [metas, setMetas] = useState<TikMeta[] | null>(null);
  const [loading, setLoading] = useState(true);

  // modal state
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [modalMode, setModalMode] = useState<"poster" | "embed">("poster"); // poster first, embed after Play
  const [embedHtml, setEmbedHtml] = useState<string | null>(null);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);

  // local fallback thumbnails (public/)
  const LOCAL_THUMBS = ["/image/tiktok1.png", "/image/tiktok2.png", "/image/tiktok3.png"];
  const GLOBAL_FALLBACK = "/image/tiktok1.png"; // ultimate fallback

  // load three thumbnails/titles from server-side endpoint
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setMetas(null);

    async function fetchMeta(url: string) {
      try {
        const res = await fetch(`/api/tiktok-meta?url=${encodeURIComponent(url)}`, {
          signal: controller.signal,
        });
        const body = await res.json().catch(() => null);
        if (!res.ok) {
          return {
            url,
            thumbnail: body?.thumbnail || null,
            title: body?.title || null,
            description: body?.description || null,
            author_name: body?.author_name || null,
            author_avatar: body?.author_avatar || null,
            error: body?.error || `status ${res.status}`,
          } as TikMeta;
        }
        return {
          url: body?.url || url,
          thumbnail: body?.thumbnail || null,
          title: body?.title || null,
          description: body?.description || null,
          author_name: body?.author_name || null,
          author_avatar: body?.author_avatar || null,
          error: body?.error || null,
        } as TikMeta;
      } catch (e: any) {
        if (controller.signal.aborted) {
          return { url, thumbnail: null, title: null, description: null, error: "aborted" } as TikMeta;
        }
        return { url, thumbnail: null, title: null, description: null, error: e?.message || "fetch error" } as TikMeta;
      }
    }

    async function loadAll() {
      try {
        const posts = (tiktokPosts || DEFAULT_TIKTOKS).slice(0, 3).map((u) => {
          try {
            const p = new URL(u);
            return `${p.protocol}//${p.host}${p.pathname}`;
          } catch {
            return u;
          }
        });
        const out: TikMeta[] = [];
        for (let i = 0; i < posts.length; i++) {
          if (controller.signal.aborted) break;
          // avoid hammering endpoint
          if (i > 0) await new Promise((r) => setTimeout(r, 120));
          const m = await fetchMeta(posts[i]);
          out.push(m);
        }
        if (!cancelled) setMetas(out);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAll();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [tiktokPosts]);

  // load oEmbed only when user requests (we'll call from click handler)
  async function loadEmbedForIndex(idx: number) {
    const url = metas && metas[idx] ? metas[idx].url : (tiktokPosts || DEFAULT_TIKTOKS)[idx];
    if (!url) {
      setEmbedError("ไม่พบลิงก์วิดีโอ");
      return;
    }

    setEmbedLoading(true);
    setEmbedError(null);
    setEmbedHtml(null);

    try {
      const oe = `https://www.tiktok.com/oembed?url=${encodeURIComponent(url)}`;
      const res = await fetch(oe);
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`oEmbed failed: ${res.status} ${txt}`);
      }
      const j = await res.json();
      const html = j.html as string | undefined;
      if (!html) throw new Error("oEmbed คืนค่า html ไม่พบ");
      setEmbedHtml(html);
      setModalMode("embed");
      // script execution handled in effect below
    } catch (err: any) {
      console.error("oembed error:", err);
      setEmbedError(String(err?.message || err) || "ไม่สามารถโหลดตัวเล่น");
    } finally {
      setEmbedLoading(false);
    }
  }

  // ensure tiktok embed script runs and inline <script> executes after embedHtml set
  useEffect(() => {
    if (!embedHtml) return;
    const t = setTimeout(() => {
      if (typeof window === "undefined") return;
      const container = modalRef.current;
      if (!container) return;

      try {
        const has = Array.from(document.scripts).some((s) => (s.src || "").includes("tiktok.com/embed.js"));
        if (!has) {
          const s = document.createElement("script");
          s.src = "https://www.tiktok.com/embed.js";
          s.async = true;
          document.body.appendChild(s);
        }
      } catch (e) {
        console.warn("append embed.js failed", e);
      }

      const scripts = Array.from(container.querySelectorAll("script"));
      scripts.forEach((old) => {
        try {
          const newScript = document.createElement("script");
          for (let i = 0; i < old.attributes.length; i++) {
            const a = old.attributes[i];
            newScript.setAttribute(a.name, a.value);
          }
          newScript.text = old.textContent || "";
          old.parentNode?.replaceChild(newScript, old);
        } catch (e) {
          // ignore
        }
      });
    }, 60);

    return () => clearTimeout(t);
  }, [embedHtml]);

  // close modal on ESC
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpenIndex(null);
        setModalMode("poster");
        setEmbedHtml(null);
        setEmbedError(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section className="bg-white py-12 ">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-800">ติดตามเรา</h2> 
          <Typography {...({} as any)} variant="lead" className="mx-auto text-lg text-gray-800 tracking-wide">
          ติดตามโซเชียลมีเดียของ <span className="text-[#2998E2]">Rebott</span> ได้ที่ช่องทาง TikTok และ Facebook เพื่ออัพเดตข่าวสารใหม่ๆ
            </Typography>
        </div> 

{/* --- โซเชียลสองคอลัมน์: มือถือ 1 คอลัมน์, เดสก์ท็อป 2 คอลัมน์ --- */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">

  {/* TikTok card */}
  <div className="rounded-xl border shadow-sm bg-white overflow-hidden h-full flex flex-col">
    <div className="px-4 sm:px-4 py-3 flex items-center justify-between">
      <h2 className="flex items-center gap-2 whitespace-nowrap text-lg sm:text-xl font-semibold text-gray-800">
        <FaTiktok className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
        <span>TikTok</span>
      </h2>
      <a
        href={tiktokProfileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm px-3 py-1.5 rounded-lg bg-[#2998E2] text-white hover:bg-[#1f86c9] transition"
      >
        ไปที่ TikTok
      </a>
    </div>

    {/* body: ความสูง “เท่ากัน” กับฝั่ง Facebook */}
    <style jsx global>{`
      .tiktok-embed {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        margin: 0 !important;
      }
    `}</style>
    <div className="px-4 sm:px-4 pb-4 flex-1">
      {/* ใช้ความสูงคงที่เหมือนกันทั้งสองฝั่ง */}
      <div className="h-[360px] md:h-[420px] lg:h-[480px] overflow-hidden rounded-lg">
      <div
      dangerouslySetInnerHTML={{
              __html: `
      <blockquote class="tiktok-embed"
        cite="${tiktokProfileUrl}"
        data-unique-id="${safeTikUser}"
        data-embed-type="creator"
        style="width:100%;max-width:100%;min-width:0;margin:0;">
        <section>
          <a target="_blank" href="${tiktokProfileUrl}?refer=creator_embed">@${safeTikUser}</a>
        </section>
      </blockquote>`,
            }}
          />
      </div>
    </div>
  </div>

  {/* โหลดสคริปต์ของ TikTok (lazy) */}
  <Script src="https://www.tiktok.com/embed.js" strategy="lazyOnload" />

  {/* Facebook card */}
  <div className="rounded-xl border shadow-sm bg-white overflow-hidden h-full flex flex-col">
    <div className="px-4 sm:px-4 py-3 flex items-center justify-between">
      <h2 className="flex items-center gap-2 whitespace-nowrap text-lg sm:text-xl font-semibold text-gray-800">
        <FontAwesomeIcon icon={faSquareFacebook} className="w-5 h-5 sm:w-6 sm:h-6 text-[#2998E2]" />
        <span>Facebook Page</span>
      </h2>
      <a
        href={facebookPage}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm px-3 py-1.5 rounded-lg bg-[#2998E2] text-white hover:bg-[#1f86c9] transition"
      >
        ไปที่เพจ Rebott
      </a>
    </div>

    {/* body: สูงเท่ากัน + จัดให้อยู่ “กลางการ์ด” */}

    <div className="px-4 sm:px-6 pb-4 flex-1">
      <div className="h-[360px] md:h-[420px] lg:h-[480px] flex items-center justify-center">
        <div className="relative w-full h-full max-w-[720px] mx-auto">  
          <iframe
            title="facebook-page"
            src={`https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(
              facebookPage
            )}&tabs=timeline&width=9999&height=500&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true`}
            style={{
              border: "none",
              overflow: "hidden",
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
            }}
            scrolling="no"
            frameBorder={0}
            allow="encrypted-media"
            loading="lazy"
          />
        </div>
      </div>
    </div>


  </div>
</div>
      </div>

  
    </section>
  );
}
